import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { isAxiosError } from 'axios';

import { getCandidateV2Options } from 'client/@tanstack/react-query.gen';
import { NotFound } from 'pages/NotFound';

/**
 * Old address of the candidate page. The page lives at
 * `/people/:identityId` now, with the same tab paths, so every
 * `/candidates/:candidateId/...` link forwards there with its tab, query
 * string and hash kept. The routes under this one exist only so those links
 * keep their types until they are repointed.
 */
export const Route = createFileRoute(
  '/_private/_route/_internal/candidates/$candidateId',
)({
  // A notFound() thrown in beforeLoad always goes to the root route, which
  // drops the app shell. So beforeLoad only records that there is nothing to
  // show, and the loader throws, which this notFoundComponent catches.
  notFoundComponent: NotFound,
  beforeLoad: async ({ context, location, params, preload }) => {
    // Always fresh: right after a merge the cached record still looks
    // unmerged, and the forward must see the merge.
    const candidateOptions = {
      ...getCandidateV2Options({
        path: {
          candidate_uuid: params.candidateId,
        },
      }),
      staleTime: 0,
    };

    // A hover only starts the request; the click joins it. A preload must
    // never redirect: this router leaves the match it redirected from stuck,
    // and the click that follows never loads.
    if (preload) {
      void context.queryClient.prefetchQuery(candidateOptions);
      return { candidateNotFound: false };
    }

    const candidate = await context.queryClient
      .fetchQuery(candidateOptions)
      .catch((error: unknown) => {
        // the api answers 404 for an unknown or malformed id
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        throw error;
      });

    if (!candidate) {
      return { candidateNotFound: true };
    }

    // A candidate merged into another one keeps no identity; its survivor
    // holds the data. One hop is enough: merging re-points earlier retirees
    // at each new survivor.
    if (candidate.merged_into_uuid) {
      throw redirect({
        href: location.href.replace(
          `/candidates/${params.candidateId}`,
          `/candidates/${candidate.merged_into_uuid}`,
        ),
        replace: true,
      });
    }

    if (!candidate.identity_uuid) {
      return { candidateNotFound: true };
    }

    // Anchored on `/candidates/<id>` so a second uuid in the path, as in
    // /candidates/<a>/compare/<b>, is left alone.
    throw redirect({
      href: location.href.replace(
        `/candidates/${params.candidateId}`,
        `/people/${candidate.identity_uuid}`,
      ),
      replace: true,
    });
  },
  loader: ({ context }) => {
    if (context.candidateNotFound) {
      throw notFound();
    }
  },
});
